import { useCallback } from 'react';
import {
  disableDebugMode,
  enableDebugMode,
  isGAEnabled,
  setFunnelSession,
  setUserProperty,
  trackButtonClick,
  trackError,
  trackEvent,
  trackFormSubmission,
  trackFunnelAbandonment,
  trackFunnelComplete,
  trackFunnelStart,
  trackFunnelStep,
  trackPageView,
  trackPerformance,
  trackPurchase,
  updateConsent,
} from '@/lib/analytics';

export const useAnalytics = () => {
  const trackCustomEvent = useCallback(
    (action: string, category: string, label?: string, value?: number) => {
      trackEvent(action, category, label, value);
    },
    []
  );

  const trackPage = useCallback((url: string, title?: string) => {
    trackPageView(url, title);
  }, []);

  const setUser = useCallback((property: string, value: string) => {
    setUserProperty(property, value);
  }, []);

  const trackPurchaseEvent = useCallback(
    (
      transactionId: string,
      value: number,
      currency: string = 'USD',
      items?: Array<{
        item_id: string;
        item_name: string;
        price: number;
        quantity: number;
      }>
    ) => {
      trackPurchase(transactionId, value, currency, items);
    },
    []
  );

  const trackForm = useCallback((formName: string, formId?: string) => {
    trackFormSubmission(formName, formId);
  }, []);

  const trackButton = useCallback(
    (buttonName: string, buttonLocation?: string) => {
      trackButtonClick(buttonName, buttonLocation);
    },
    []
  );

  const trackErrorEvent = useCallback(
    (errorMessage: string, errorCode?: string) => {
      trackError(errorMessage, errorCode);
    },
    []
  );

  const trackPerformanceMetric = useCallback(
    (metric: string, value: number) => {
      trackPerformance(metric, value);
    },
    []
  );

  const updateConsentSettings = useCallback((consent: boolean) => {
    updateConsent(consent);
  }, []);

  const enableDebug = useCallback(() => {
    enableDebugMode();
  }, []);

  const disableDebug = useCallback(() => {
    disableDebugMode();
  }, []);

  const isEnabled = useCallback(() => {
    return isGAEnabled();
  }, []);

  // dedicated funnel tracking
  const trackFunnelStepEvent = useCallback(
    (
      stepName: string,
      stepNumber: number,
      hash?: string,
      data?: Record<string, any>
    ) => {
      trackFunnelStep(stepName, stepNumber, hash, data);
    },
    []
  );

  const startFunnel = useCallback((hash: string, eventName?: string) => {
    trackFunnelStart(hash, eventName);
  }, []);

  const completeFunnel = useCallback(
    (hash: string, eventsSelected?: string[]) => {
      trackFunnelComplete(hash, eventsSelected);
    },
    []
  );

  const abandonFunnel = useCallback(
    (stepName: string, stepNumber: number, reason?: string) => {
      trackFunnelAbandonment(stepName, stepNumber, reason);
    },
    []
  );

  const setFunnelSessionData = useCallback((hash: string, eventId?: string) => {
    setFunnelSession(hash, eventId);
  }, []);

  return {
    trackEvent: trackCustomEvent,
    trackPage,
    setUser,
    trackPurchase: trackPurchaseEvent,
    trackForm,
    trackButton,
    trackError: trackErrorEvent,
    trackPerformance: trackPerformanceMetric,
    updateConsent: updateConsentSettings,
    enableDebug,
    disableDebug,
    isEnabled,
    // dedicated funnel tracking methods
    trackFunnelStep: trackFunnelStepEvent,
    startFunnel,
    completeFunnel,
    abandonFunnel,
    setFunnelSession: setFunnelSessionData,
  };
};
